import { Injectable } from '@angular/core';
import { AdyenWebSdk } from '@models/adyen.model';
import { LoggerService } from '@services/utils/logger.service';

/**
 * Loads the Adyen Web SDK from Adyen's CDN, once per page.
 *
 * Loaded at runtime rather than bundled for two reasons. First, the widget ships
 * to every partner and only a handful use Adyen — bundling ~300 kB of PSP SDK
 * into everyone's booking page to serve a minority is the wrong trade. Second,
 * it mirrors how Stripe.js is already brought in (`js.stripe.com/v3` injected on
 * demand), so there is one story for "third-party payment SDK" in this codebase
 * rather than two.
 *
 * The version is pinned. An unpinned CDN URL means Adyen can change the code
 * running inside a payment form on thousands of partner domains without anyone
 * here deciding to accept it; bumping it is a deliberate act.
 */
@Injectable({ providedIn: 'root' })
export class AdyenLoaderService {

  /**
   * Pinned Adyen Web version.
   *
   * When bumping: read the release notes for breaking changes (v6 changed the
   * mount API from `checkout.create('dropin')` to `new Dropin(checkout)`), and
   * consider adding the SRI `integrity` hash Adyen publishes alongside each
   * release — this loader has no integrity attribute today.
   */
  private static readonly SDK_VERSION = '6.13.1';

  /**
   * How long the script may take before the load counts as failed. A CDN request that neither
   * loads nor errors (a stalled connection, a proxy holding it open) fires no event at all, so
   * without this the payment form shows "Loading payment methods" for ever, with no Retry.
   */
  private static readonly LOAD_TIMEOUT_MS = 20_000;

  /**
   * The give-up time in force. A host page may shorten it through `window.__calensoAdyenLoadTimeoutMs`
   * (a positive number of milliseconds) - the hermetic E2E suite does, so the stalled-download case
   * runs in seconds instead of waiting out the production value.
   */
  private static get loadTimeoutMs(): number {
    const override = (window as unknown as { __calensoAdyenLoadTimeoutMs?: unknown }).__calensoAdyenLoadTimeoutMs;

    return typeof override === 'number' && override > 0 ? override : AdyenLoaderService.LOAD_TIMEOUT_MS;
  }

  /**
   * One promise per environment, so N components mounting at once cause one
   * network fetch and every caller waits on the same result. Re-injecting the
   * SDK is not merely wasteful — a second copy re-registers custom elements and
   * throws.
   */
  private readonly loaders = new Map<string, Promise<void>>();

  /**
   * @param environment 'test' or 'live' — they are different CDN hosts, and a
   *                    live client key against the test host silently fails.
   */
  load(environment: 'test' | 'live'): Promise<void> {
    const existing = this.loaders.get(environment);
    if (existing) {
      return existing;
    }

    const promise = new Promise<void>((resolve, reject) => {
      try {
        // The canonical CDN hosts. `checkoutshopper-test.adyen.com` answers a 302
        // to `checkoutshopper-test.cdn.adyen.com`; a browser follows it, but
        // naming the real host saves every shopper a redirect on the critical
        // path and keeps the URL identical to what the CSP would need to allow.
        const base = environment === 'live'
          ? 'https://checkoutshopper-live.cdn.adyen.com/checkoutshopper'
          : 'https://checkoutshopper-test.cdn.adyen.com/checkoutshopper';
        const version = AdyenLoaderService.SDK_VERSION;
        const scriptUrl = `${base}/sdk/${version}/adyen.js`;
        const styleUrl = `${base}/sdk/${version}/adyen.css`;

        // The host page may already carry the SDK (a partner running their own
        // Adyen integration alongside the widget). Adopt it rather than adding
        // a second copy.
        if ((window as unknown as { AdyenWeb?: unknown }).AdyenWeb) {
          resolve();
          return;
        }

        if (!document.querySelector(`link[href="${styleUrl}"]`)) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = styleUrl;
          document.head.appendChild(link);
        }

        const alreadyInjected = document.querySelector<HTMLScriptElement>(`script[src="${scriptUrl}"]`);
        if (alreadyInjected) {
          // Someone else's tag (the host page, or another widget on it). It is waited for, never
          // removed: the loader only ever deletes what it injected itself. The wait is bounded
          // like our own, or a tag that stalls would hang every caller with no Retry.
          const onLoad = (): void => settle(() => resolve());
          const onError = (): void => settle(() => reject(new Error('Adyen SDK failed to load')));
          const settle = (outcome: () => void): void => {
            clearTimeout(timeout);
            alreadyInjected.removeEventListener('load', onLoad);
            alreadyInjected.removeEventListener('error', onError);
            outcome();
          };
          const timeout = setTimeout(
            () => settle(() => reject(new Error('Adyen SDK load timed out'))),
            AdyenLoaderService.loadTimeoutMs,
          );
          alreadyInjected.addEventListener('load', onLoad);
          alreadyInjected.addEventListener('error', onError);
          return;
        }

        const script = document.createElement('script');
        // Both failure paths drop the tag, so the next load() - the customer's Retry - injects a
        // fresh one instead of waiting on a dead one. The handlers are detached first: a stalled
        // tag that errors AFTER the timeout must not act again, because by then Retry may have
        // injected and cached a new load that a second give-up would disturb.
        const giveUp = (reason: string): void => {
          clearTimeout(timeout);
          script.onload = null;
          script.onerror = null;
          // Leaving a failed tag in the DOM would make the `alreadyInjected`
          // branch above wait on a script that will never load.
          script.remove();
          reject(new Error(reason));
        };
        const timeout = setTimeout(() => giveUp('Adyen SDK load timed out'), AdyenLoaderService.loadTimeoutMs);
        script.src = scriptUrl;
        script.async = true;
        script.onload = () => {
          clearTimeout(timeout);
          resolve();
        };
        script.onerror = () => giveUp('Adyen SDK failed to load');
        document.head.appendChild(script);
      } catch (error) {
        LoggerService.error(error);
        reject(error instanceof Error ? error : new Error('Adyen SDK failed to load'));
      }
    });

    this.loaders.set(environment, promise);
    // Every failure forgets its own promise - and only its own: by the time a late one lands,
    // Retry may already have cached a newer load, which must survive.
    promise.catch(() => {
      if (this.loaders.get(environment) === promise) {
        this.loaders.delete(environment);
      }
    });

    return promise;
  }

  /**
   * The global the SDK publishes, or null when it has not loaded.
   *
   * Returns null rather than throwing so a caller can fail with a translated
   * message instead of an unhandled rejection in a payment form.
   */
  getSdk(): AdyenWebSdk | null {
    const sdk = (window as unknown as { AdyenWeb?: Partial<AdyenWebSdk> }).AdyenWeb;
    if (!sdk?.AdyenCheckout || !sdk?.Dropin) {
      return null;
    }

    return { AdyenCheckout: sdk.AdyenCheckout, Dropin: sdk.Dropin };
  }
}
